/**
 * useEscapeKey Hook
 *
 * Triggers a callback when the Escape key is pressed.
 * Commonly used for closing modals, dropdowns, and dismissing overlays.
 *
 * @example
 * ```tsx
 * useEscapeKey(() => setIsOpen(false));
 * ```
 */

import { useEffect, useCallback } from "react";

/**
 * Hook that triggers a callback when the Escape key is pressed.
 *
 * @param callback - Function to call when Escape key is pressed
 * @param enabled - Optional flag to enable/disable the listener (default: true)
 */
export function useEscapeKey(
  callback: () => void,
  enabled: boolean = true,
): void {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        callback();
      }
    },
    [callback],
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleEscape, enabled]);
}
